// SEO copy for the public Sebapoznanie test landing pages (/sebapoznanie/<slug>) and the hub (/sebapoznanie).
// Shared by the Nuxt app and the Nitro server (sitemap) – keep this file free of app/ imports.
// Facts (durations, question counts, models) must stay in sync with useSebapoznanieTests.ts,
// useTestLandingContent.ts and tallyData.ts.

export interface TestFaqItem {
    question: string
    /** Plain text, 1–3 sentences. No HTML, no markdown. */
    answer: string
}

export interface TestSeoContent {
    /** <title>. Max 60 characters INCLUDING the suffix " | GROWNi". Lead with the main search phrase. */
    title: string
    /** Meta description. 130–155 characters. Benefit + what you get + "zadarmo" where natural + soft CTA. */
    description: string
    /** Search phrases this page targets (reference only, not rendered). Main phrase first. */
    keywords: string[]
    /** One short intro paragraph (2–3 sentences, ~300–450 chars) rendered under the H1, using the main phrase naturally once. */
    intro: string
    /** 4–6 questions. Mix test-specific (what it measures, how to interpret, how it differs from X) with practical (duration, free, privacy). */
    faq: TestFaqItem[]
    /** Exactly 3 slugs of related tests for internal links (must be slugs from the list below, not itself). */
    related: string[]
}

export interface HubSeoContent {
    title: string
    description: string
    /** H1 for /sebapoznanie — must contain "test osobnosti"/"testy osobnosti" naturally, max ~60 chars. */
    h1: string
    /** Subtitle under H1, 1–2 sentences. */
    subtitle: string
    /** 6–8 FAQ items. */
    faq: TestFaqItem[]
}

/** Keyed by URL slug. Every public test MUST have an entry — the sitemap is generated from these keys. */
export const SEBAPOZNANIE_TEST_SEO: Record<string, TestSeoContent> = {
    'osobnostny-dotaznik': {
        title: 'Osobnostný test zadarmo – profil v 8 oblastiach | GROWNi',
        description:
            'Osobnostný test zadarmo: 92 otázok, asi 15 minút. Zistíš, ako si na tom s výkonom, svedomitosťou, tímovou prácou či sebareflexiou. Výsledok hneď.',
        keywords: [
            'osobnostný test',
            'test osobnosti',
            'osobnostný dotazník',
            'test osobnosti zadarmo',
            'osobnostný test online'
        ],
        intro: 'Osobnostný dotazník je náš najpodrobnejší osobnostný test. Za približne 15 minút odpovieš na 92 otázok a dostaneš profil v ôsmich oblastiach – od orientácie na výkon a svedomitosti cez tímovú prácu a sebareflexiu až po morálne uvedomenie. Pomôže ti lepšie pochopiť, ako funguješ v práci, v tíme aj medzi ľuďmi.',
        faq: [
            {
                question: 'Čo meria osobnostný dotazník?',
                answer: 'Dotazník ti ukáže profil v ôsmich oblastiach: orientácia na výkon, svedomitosť, tímová práca, vnímanie vlastných emócií, sebareflexia, odviazanosť, tienisté stránky osobnosti a morálne uvedomenie. Pri každej oblasti uvidíš, kde sa nachádzaš, aj krátku interpretáciu.'
            },
            {
                question: 'Aký je rozdiel medzi osobnostným dotazníkom a testom Big Five?',
                answer: 'Big Five je rýchly test (30 otázok, asi 3 minúty), ktorý meria päť základných dimenzií osobnosti a porovná ťa so slovenskou populáciou. Osobnostný dotazník je dlhší (92 otázok, asi 15 minút) a zameriava sa na to, ako funguješ v práci a medzi ľuďmi. Najviac sa dozvieš, keď si urobíš oba.'
            },
            {
                question: 'Koľko trvá test a je naozaj zadarmo?',
                answer: 'Vyplnenie trvá približne 15 minút a test je zadarmo. Potrebuješ len bezplatný účet na GROWNi, do ktorého sa ti výsledky uložia, a uvidíš ich hneď po dokončení.'
            },
            {
                question: 'Ako mám odpovedať, aby bol výsledok presný?',
                answer: 'Neexistujú správne ani nesprávne odpovede. Odpovedaj podľa toho, ako sa naozaj správaš, nie podľa toho, čo by znelo najlepšie – príliš ideálne odpovede výsledok skresľujú.'
            },
            {
                question: 'Kto uvidí moje výsledky?',
                answer: 'Výsledky vidíš iba ty. Test môžeš kedykoľvek zopakovať a predchádzajúce výsledky ti zostanú uložené.'
            },
            {
                question: 'Je osobnostný dotazník psychologická diagnostika?',
                answer: 'Nie. Ide o nástroj na sebapoznanie a sebareflexiu, nie o klinické vyšetrenie. Ak riešiš vážnejšie ťažkosti, obráť sa na psychológa.'
            }
        ],
        related: ['big-five', 'hodnoty', 'karierne-zaujmy']
    },

    'big-five': {
        title: 'Big Five test osobnosti zadarmo – za 3 minúty | GROWNi',
        description:
            'Big Five test zadarmo: 30 otázok, asi 3 minúty. Zistíš svoju otvorenosť, svedomitosť, extraverziu, prívetivosť a emocionalitu oproti slovenskej populácii.',
        keywords: [
            'big five test',
            'big five',
            'big five personality test',
            'päťfaktorový model osobnosti',
            'OCEAN test osobnosti',
            'test osobnosti big five'
        ],
        intro: 'Big Five test vychádza z päťfaktorového modelu osobnosti (OCEAN), ktorý je dnes vo vedeckej psychológii najrozšírenejším spôsobom, ako opísať osobnosť. Používame slovenskú verziu krátkeho dotazníka BFI-2-S: za približne 3 minúty odpovieš na 30 tvrdení a uvidíš, kde sa v každej z piatich dimenzií nachádzaš v porovnaní so slovenskou populáciou.',
        faq: [
            {
                question: 'Čo meria Big Five test?',
                answer: 'Päť základných dimenzií osobnosti, známych aj ako OCEAN: otvorenosť, svedomitosť, extraverziu, prívetivosť a negatívnu emocionalitu (jej opakom je emocionálna stabilita). Spolu opisujú, ako premýšľaš, pracuješ, zvládaš stres a správaš sa k ľuďom.'
            },
            {
                question: 'Ako mám čítať výsledky Big Five?',
                answer: 'Pri každej dimenzii dostaneš skóre STEN na škále od 1 do 10 a percentil, ktorý ukazuje, ako si na tom v porovnaní so slovenskou populáciou. Vysoké ani nízke skóre nie je dobré alebo zlé – každý pól má svoje silné stránky aj riziká.'
            },
            {
                question: 'Aký je rozdiel medzi Big Five a MBTI alebo 16 Personalities?',
                answer: 'MBTI ťa zaradí do jedného zo 16 typov, Big Five ťa opisuje na piatich plynulých škálach – väčšina ľudí totiž nie je čistý introvert ani extrovert, ale niekde medzi. Big Five má vo vedeckom výskume výrazne silnejšiu oporu, preto ho používame namiesto MBTI.'
            },
            {
                question: 'Aký dotazník test používa?',
                answer: 'Ide o BFI-2-S, krátku verziu dotazníka Big Five Inventory-2 od psychológov Christophera Sota a Olivera Johna, v slovenskej adaptácii. K modelu Big Five zásadne prispeli aj Lewis Goldberg, Paul Costa a Robert McCrae.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 30 tvrdení a zaberie asi 3 minúty. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok uvidíš hneď po vyplnení.'
            },
            {
                question: 'Kto uvidí moje výsledky?',
                answer: 'Výsledky vidíš iba ty. Keď sa cez GROWNi uchádzaš o prácu, môžeš sa rozhodnúť, či zamestnávateľovi ukážeš zhrnutie výsledkov dôležitých pre kariéru – je to úplne dobrovoľné.'
            }
        ],
        related: ['osobnostny-dotaznik', 'karierne-zaujmy', 'charakterove-silne-stranky']
    },

    'moralny-vyvoj': {
        title: 'Test morálneho vývoja podľa Kohlberga zadarmo | GROWNi',
        description:
            'Riešiš morálne dilemy a zistíš, na ktorom zo 6 Kohlbergových štádií morálneho uvažovania sa najčastejšie pohybuješ. Test je zadarmo, výsledok hneď.',
        keywords: [
            'test morálneho vývoja',
            'Kohlberg morálny vývoj',
            'Kohlbergove štádiá morálneho vývoja',
            'morálne dilemy test',
            'Heinzova dilema'
        ],
        intro: 'Test morálneho vývoja vychádza z teórie psychológa Lawrencea Kohlberga, podľa ktorej sa morálne uvažovanie vyvíja v šiestich štádiách. Prejdeš približne 20 dilemami bez jednoduchej odpovede a zistíš, či sa pri rozhodovaní opieraš skôr o pravidlá, o očakávania okolia, alebo o všeobecné princípy spravodlivosti.',
        faq: [
            {
                question: 'Čo je Kohlbergova teória morálneho vývoja?',
                answer: 'Lawrence Kohlberg z Harvardu opísal tri úrovne morálneho uvažovania – predkonvenčnú, konvenčnú a postkonvenčnú – a v každej dve štádiá. Vedú od poslušnosti a vlastného záujmu cez medziľudskú zhodu, autoritu a poriadok až po spoločenskú zmluvu a univerzálne princípy.'
            },
            {
                question: 'Ako test prebieha a koľko trvá?',
                answer: 'Čítaš približne 20 krátkych morálnych dilem, napríklad klasickú Heinzovu dilemu, a pri každej vyberieš odpoveď, ktorá obsahuje rozhodnutie aj jeho zdôvodnenie. Trvá to asi 15 minút. Ak výsledok nie je jednoznačný, môžeš si dobrovoľne zodpovedať niekoľko ďalších dilem, ktoré ho spresnia.'
            },
            {
                question: 'Existujú v dilemách správne odpovede?',
                answer: 'Nie. Dilemy sú zámerne postavené tak, aby nemali jednoduché riešenie. Pre výsledok je dôležitejšie zdôvodnenie, ktoré ti je najbližšie, než samotné rozhodnutie.'
            },
            {
                question: 'Znamená vyššie štádium, že som lepší človek?',
                answer: 'Nie. Test neposudzuje tvoju morálnu hodnotu, ale spôsob, akým o etických otázkach uvažuješ. Carol Gilligan navyše upozornila, že etika starostlivosti o konkrétnych ľudí je rovnako hodnotná, aj keď ju Kohlbergov model plne nezachytí.'
            },
            {
                question: 'Je test zadarmo a kto uvidí výsledky?',
                answer: 'Test je zadarmo, stačí bezplatný účet na GROWNi. Výsledok uvidíš hneď po dokončení a vidíš ho iba ty. Test môžeš kedykoľvek zopakovať, predchádzajúce výsledky zostanú uložené.'
            }
        ],
        related: ['hodnoty', 'dospely-vyvoj', 'urovne-vedomia']
    },

    'hodnoty': {
        title: 'Test hodnôt zadarmo – čo je pre teba dôležité | GROWNi',
        description:
            'Test hodnôt podľa Shaloma Schwartza: 20 otázok, asi 6 minút. Zistíš, ktoré z 10 univerzálnych hodnôt ťa najviac vedú pri rozhodovaní. Zadarmo.',
        keywords: [
            'test hodnôt',
            'životné hodnoty test',
            'Schwartzove hodnoty',
            'Schwartz test hodnôt',
            'osobné hodnoty'
        ],
        intro: 'Test hodnôt vychádza z teórie sociálneho psychológa Shaloma Schwartza, ktorý opísal 10 základných hodnôt prítomných vo všetkých kultúrach. Za približne 6 minút zistíš, na čom ti v živote najviac záleží, ktoré hodnoty sa u teba navzájom dopĺňajú a kde môžeš cítiť vnútorné napätie pri dôležitých rozhodnutiach.',
        faq: [
            {
                question: 'Aké hodnoty test meria?',
                answer: 'Desať hodnôt podľa Schwartza: moc, výkon, hedonizmus, stimulácia, sebariadenie, univerzalizmus, benevolencia, tradícia, konformita a bezpečnosť. Výsledok ukáže, ktoré z nich sú pre teba prioritou.'
            },
            {
                question: 'Ako mám čítať výsledok testu hodnôt?',
                answer: 'Schwartz usporiadal hodnoty do kruhu: susedné hodnoty sa dopĺňajú, protiľahlé si konkurujú, napríklad sebariadenie a konformita. Výsledok preto neukazuje, či máš hodnoty dobré alebo zlé, ale aké máš priority a kde môžeš pociťovať napätie.'
            },
            {
                question: 'Prečo je dobré poznať svoje hodnoty?',
                answer: 'Hodnoty ovplyvňujú, akú prácu si vyberáš, s kým si rozumieš a pri akých rozhodnutiach váhaš. Keď ich vieš pomenovať, ľahšie spoznáš prostredie a ľudí, ktorí ti sedia.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 otázok a zaberie asi 6 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok uvidíš hneď. Vidíš ho iba ty.'
            },
            {
                question: 'Môžu sa moje hodnoty časom zmeniť?',
                answer: 'Základné hodnoty sú pomerne stabilné, no ich poradie sa môže posunúť s veľkými životnými zmenami. Test môžeš kedykoľvek zopakovať a predchádzajúce výsledky ti zostanú uložené.'
            }
        ],
        related: ['motivacia', 'charakterove-silne-stranky', 'moralny-vyvoj']
    },

    'karierne-zaujmy': {
        title: 'Test povolania zadarmo – aké povolanie sa ti hodí | GROWNi',
        description:
            'Test povolania podľa Hollanda (RIASEC): 72 otázok, asi 15 minút. Získaš svoj trojpísmenový kód a kariérne oblasti, ktoré ti sedia. Zadarmo, výsledok hneď.',
        keywords: [
            'test povolania',
            'test na povolanie',
            'kariérny test',
            'aké povolanie sa ku mne hodí',
            'Holland RIASEC test',
            'test kariérnych záujmov'
        ],
        intro: 'Test povolania vychádza z Hollandovho modelu RIASEC, jedného z najpoužívanejších nástrojov kariérneho poradenstva. Ohodnotíš pracovné činnosti podľa toho, či by ťa bavili, a dostaneš svoj trojpísmenový kód spolu s kariérnymi oblasťami a pracovným prostredím, ktoré zodpovedajú tvojim záujmom. Hodí sa pri výbere školy aj pri zmene práce.',
        faq: [
            {
                question: 'Čo znamená RIASEC?',
                answer: 'Psychológ John Holland opísal šesť typov kariérnych záujmov: realistický (R), výskumný (I), umelecký (A), sociálny (S), podnikavý (E) a konvenčný (C). Tvoj kód tvoria tri typy, ktoré sú u teba najsilnejšie, napríklad SEC alebo RIA.'
            },
            {
                question: 'Povie mi test, aké povolanie si mám vybrať?',
                answer: 'Test ti ukáže kariérne oblasti a typ práce, ktoré zodpovedajú tvojim záujmom. Záujmy sú však len jedna časť rozhodnutia, preto výsledok porovnaj aj so svojimi schopnosťami a hodnotami – a pokojne ho preber s mentorom na GROWNi.'
            },
            {
                question: 'Pre koho je test povolania vhodný?',
                answer: 'Pre stredoškolákov, ktorí vyberajú vysokú školu, pre študentov pred prvou prácou aj pre každého, kto zvažuje zmenu kariéry. Neexistujú správne ani nesprávne odpovede – ide len o to, čo ťa baví.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 72 otázok a zaberie približne 15 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok uvidíš hneď po vyplnení.'
            },
            {
                question: 'Uvidí môj výsledok zamestnávateľ?',
                answer: 'Iba ak budeš chcieť. Výsledky vidíš len ty, no keď sa cez GROWNi uchádzaš o prácu, môžeš sa rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov dôležitých pre kariéru.'
            }
        ],
        related: ['big-five', 'motivacia', 'podnikavost']
    },

    'motivacia': {
        title: 'Test motivácie zadarmo – čo ťa skutočne poháňa | GROWNi',
        description:
            'Reflexia motivácie inšpirovaná SDT: 15 hodnotení v 3 oblastiach, asi 3 minúty. Preskúmaj svoje dôvody pri práci, učení a pomoci druhým. Zadarmo.',
        keywords: [
            'test motivácie',
            'vnútorná a vonkajšia motivácia',
            'teória sebadeterminácie',
            'self-determination theory test',
            'čo ma motivuje'
        ],
        intro: 'Táto vlastná reflexia GROWNi je inšpirovaná teóriou sebaurčenia (SDT) Edwarda Deciho a Richarda Ryana. Za približne 3 minúty ohodnotíš rôzne dôvody pri práci alebo štúdiu, učení a pomoci druhým. Viac dôvodov môže sedieť súčasne a oblasti, ktoré sa ťa netýkajú, preskočíš. Ide o pomôcku na zamyslenie, nie psychometricky validovaný test.',
        faq: [
            {
                question: 'Čo je teória sebadeterminácie?',
                answer: 'Podľa Deciho a Ryana motivácia nie je len „vnútorná alebo vonkajšia“, ale spektrum od amotivácie cez rôzne formy vonkajšej regulácie až po vnútornú motiváciu. Kvalitnú motiváciu podporuje naplnenie troch potrieb: autonómie, kompetencie a spolupatričnosti.'
            },
            {
                question: 'Je vonkajšia motivácia zlá?',
                answer: 'Nie. Každý má mix rôznych typov motivácie. Keď robíš niečo preto, že tomu rozumieš a považuješ to za dôležité, je to síce vonkajšia, ale stále autonómna a udržateľná motivácia.'
            },
            {
                question: 'Ako mi výsledok testu motivácie pomôže?',
                answer: 'Uvidíš, nakoľko na teba v jednotlivých oblastiach sedia odmeny, povinnosť, osobný význam, záujem a nejasnosť dôvodov. Výsledok môže byť podnetom na zamyslenie alebo rozhovor s mentorom; nemeria tvoju pohodu ani psychologické potreby.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Reflexia má 15 hodnotení v 3 oblastiach a zaberie asi 3 minúty. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok uvidíš hneď po vyplnení.'
            },
            {
                question: 'Kto uvidí moje výsledky?',
                answer: 'Výsledky vidíš iba ty. Keď sa cez GROWNi uchádzaš o prácu, môžeš sa dobrovoľne rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov dôležitých pre kariéru.'
            }
        ],
        related: ['hodnoty', 'rastove-myslenie', 'index-excelencie']
    },

    'rastove-myslenie': {
        title: 'Test rastového myslenia (growth mindset) zadarmo | GROWNi',
        description:
            'Test rastového myslenia podľa Carol Dweck: 15 otázok, asi 5 minút. Zistíš, či máš skôr rastové, alebo fixné myslenie v 5 oblastiach života. Zadarmo.',
        keywords: [
            'rastové myslenie',
            'growth mindset test',
            'test rastového myslenia',
            'fixné myslenie',
            'Carol Dweck mindset'
        ],
        intro: 'Test rastového myslenia vychádza z výskumu psychologičky Carol Dweck zo Stanfordu. Za približne 5 minút zistíš, či o svojich schopnostiach uvažuješ skôr ako o niečom, čo sa dá rozvíjať (growth mindset), alebo ako o niečom danom (fixed mindset) – a v ktorých oblastiach života, od kariéry po vzťahy, sa to u teba líši.',
        faq: [
            {
                question: 'Čo je rastové myslenie (growth mindset)?',
                answer: 'Je to presvedčenie, že schopnosti, inteligencia aj talent sa dajú rozvíjať úsilím, učením a dobrými stratégiami. Jeho opakom je fixné myslenie, podľa ktorého sú schopnosti dané a nedajú sa výrazne zmeniť.'
            },
            {
                question: 'Aký výsledok dostanem?',
                answer: 'Dozvieš sa, či máš celkovo skôr fixné, zmiešané, alebo rastové myslenie, a uvidíš ho rozpísané v piatich oblastiach: inteligencia, talent a schopnosti, vzťahy, kariéra a zdravie. Väčšina ľudí má v rôznych oblastiach iný prístup.'
            },
            {
                question: 'Dá sa myslenie zmeniť?',
                answer: 'Podľa Carol Dweck nejde o pevnú vlastnosť, ale o presvedčenie, s ktorým sa dá pracovať. Pomáha všímať si, ako reaguješ na neúspech a kritiku, a vedome vyhľadávať výzvy.'
            },
            {
                question: 'Aký je rozdiel oproti Indexu excelencie?',
                answer: 'Test rastového myslenia meria tvoje presvedčenia o tom, či sa schopnosti dajú rozvíjať. Index excelencie meria návyky ako vytrvalosť, schopnosť učiť sa či štandardy kvality, preto sa tieto dva testy dobre dopĺňajú.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 15 otázok a zaberie asi 5 minút. Je zadarmo, stačí bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['index-excelencie', 'motivacia', 'charakterove-silne-stranky']
    },

    'charakterove-silne-stranky': {
        title: 'Test silných stránok zadarmo – tvojich top 5 síl | GROWNi',
        description:
            'Test silných stránok podľa klasifikácie VIA: 96 tvrdení, asi 10 minút. Zoradíš si 24 charakterových síl a objavíš svojich top 5. Zadarmo, výsledok hneď.',
        keywords: [
            'test silných stránok',
            'charakterové silné stránky',
            'VIA silné stránky',
            'test cností',
            'signature strengths'
        ],
        intro: 'Test silných stránok vychádza z klasifikácie VIA, ktorú v rámci pozitívnej psychológie vytvorili Christopher Peterson a Martin Seligman. Na základe 96 tvrdení zoradíme 24 charakterových síl v šiestich cnostiach a ukážeme ti tvojich päť podpisových silných stránok, o ktoré sa môžeš opierať v práci aj v živote.',
        faq: [
            {
                question: 'Čo sú charakterové silné stránky?',
                answer: 'Sú to pozitívne vlastnosti, ako zvedavosť, láskavosť, vytrvalosť či vďačnosť. Klasifikácia VIA ich opisuje 24 a zoskupuje do šiestich cností: múdrosť, odvaha, ľudskosť, spravodlivosť, umiernenosť a transcendencia.'
            },
            {
                question: 'Je to oficiálny VIA test?',
                answer: 'Nie. Ide o vlastnú slovenskú verziu s pôvodnými otázkami, ktorá vychádza z klasifikácie VIA od Petersona a Seligmana. Nie je to dotazník organizácie VIA Institute on Character.'
            },
            {
                question: 'Čo sú podpisové silné stránky?',
                answer: 'Tak sa označuje päť síl, ktoré sú u teba najvýraznejšie a pôsobia najprirodzenejšie. Keď ich vedome využívaš v práci a vo vzťahoch, býva to pre teba zdrojom energie.'
            },
            {
                question: 'Sú sily na konci rebríčka moje slabiny?',
                answer: 'Nie. Rebríček ukazuje poradie tvojich síl, nie zoznam nedostatkov. Sily na konci len menej využívaš a môžeš ich zámerne rozvíjať, ak chceš.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 96 tvrdení a zaberie asi 10 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['hodnoty', 'big-five', 'rastove-myslenie']
    },

    'dospely-vyvoj': {
        title: 'Test dospelého vývoja podľa Kegana zadarmo | GROWNi',
        description:
            'Riešiš 20 situácií zo života a zistíš, ako komplexne vnímaš seba, vzťahy a svet podľa teórie Roberta Kegana. Test je zadarmo a výsledok uvidíš hneď.',
        keywords: [
            'Kegan test',
            'test dospelého vývoja',
            'Keganove štádiá vývoja',
            'adult development',
            'sebaautorská myseľ'
        ],
        intro: 'Test dospelého vývoja vychádza z teórie Roberta Kegana z Harvardu, podľa ktorej sa spôsob, akým rozumieme sebe a svetu, vyvíja aj v dospelosti. V 20 príbehových situáciách z práce, rodiny a vzťahov vyberieš reakciu, ktorá je ti najbližšia, a zistíš, na ktorej z úrovní 2 až 5 sa najčastejšie pohybuješ.',
        faq: [
            {
                question: 'Čo meria test dospelého vývoja?',
                answer: 'Úroveň, na ktorej konštruuješ zmysel seba a sveta. Test rozlišuje štyri úrovne: vlastné nástroje (2), socializovanú myseľ (3), sebaautorskú myseľ (4) a sebatransformujúcu myseľ (5). Úroveň 1 sa týka raného detstva, preto ju test nemeria.'
            },
            {
                question: 'Aký je rozdiel oproti testu Úrovne vedomia?',
                answer: 'Oba testy vychádzajú z Keganovej teórie a pokrývajú úrovne 2 až 5. Dospelý vývoj stojí na príbehových situáciách zo života, Úrovne vedomia na priamych otázkach o tom, ako prežívaš hodnoty, kritiku či vlastnú identitu, takže spolu ti dajú presnejší obraz.'
            },
            {
                question: 'Je vyššia úroveň lepšia?',
                answer: 'Nie je to známka ani hodnotenie človeka. Každá úroveň je prirodzenou fázou vývoja a podľa Kegana sa väčšina dospelých pohybuje na úrovni 3.'
            },
            {
                question: 'Dá sa posunúť na ďalšiu úroveň?',
                answer: 'Podľa Kegana vývoj pokračuje celý život, ale je pomalý. Pomáhajú mu nové výzvy, reflexia a podporné vzťahy, napríklad s mentorom alebo koučom.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 situácií a zaberie asi 12 minút. Je zadarmo, stačí bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['urovne-vedomia', 'moralny-vyvoj', 'styly-vedenia']
    },

    'vztahove-styly': {
        title: 'Test vzťahovej väzby (attachment style) zadarmo | GROWNi',
        description:
            'Test vzťahovej väzby: 16 otázok, asi 6 minút. Zistíš, či je tvoj štýl pripútania istý, úzkostný alebo vyhýbavý a čo to znamená pre tvoje vzťahy. Zadarmo.',
        keywords: [
            'attachment style test',
            'test vzťahovej väzby',
            'štýl pripútania',
            'vzťahová väzba v dospelosti',
            'úzkostný vzťahový štýl'
        ],
        intro: 'Test vzťahovej väzby (attachment style test) vychádza z teórie Johna Bowlbyho a Mary Ainsworthovej, ktorú na vzťahy dospelých rozšírili Cindy Hazan a Phillip Shaver. Za približne 6 minút zistíš, ako veľmi sa vo vzťahoch obávaš odmietnutia, nakoľko sa vyhýbaš blízkosti a aký štýl pripútania z toho vychádza.',
        faq: [
            {
                question: 'Aké štýly vzťahovej väzby existujú?',
                answer: 'Test rozlišuje štyri štýly: istý (bezpečný), úzkostný, vyhýbavý a bojazlivo-vyhýbavý, ktorý sa niekedy označuje ako dezorganizovaný. Vychádzajú z dvoch rozmerov – miery úzkosti a miery vyhýbania sa blízkosti.'
            },
            {
                question: 'Týka sa test len partnerských vzťahov?',
                answer: 'Otázky sú postavené na situáciách z partnerského vzťahu, kde sa štýl pripútania prejavuje najzreteľnejšie. Podobné vzorce si však môžeš všimnúť aj v priateľstvách či v práci.'
            },
            {
                question: 'Dá sa štýl vzťahovej väzby zmeniť?',
                answer: 'Áno, štýl pripútania nie je nemenný. Skúsenosti v bezpečných vzťahoch, sebareflexia alebo práca s odborníkom môžu postupne viesť k istejšiemu štýlu, čo sa označuje ako „získaná istota“ (earned security).'
            },
            {
                question: 'Je to psychologická diagnóza?',
                answer: 'Nie. Test je nástroj na sebapoznanie, nie klinické vyšetrenie. Ak ti vzťahy spôsobujú veľké trápenie, obráť sa na psychológa alebo terapeuta.'
            },
            {
                question: 'Koľko trvá test a kto uvidí výsledky?',
                answer: 'Test má 16 otázok a zaberie asi 6 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok vidíš iba ty.'
            }
        ],
        related: ['urovne-pocuvania', 'big-five', 'hodnoty']
    },

    'podnikavost': {
        title: 'Test podnikavosti zadarmo – máš na podnikanie? | GROWNi',
        description:
            'Test podnikavosti: 35 otázok, asi 3 minúty. Zmeriaš iniciatívu, ochotu riskovať, inovatívnosť, riešenie problémov aj občiansku angažovanosť. Zadarmo.',
        keywords: [
            'test podnikavosti',
            'podnikateľský test',
            'podnikateľský potenciál',
            'mám na podnikanie',
            'entrepreneurship test'
        ],
        intro: 'Test podnikavosti meria predpoklady, ktoré sú dôležité pri rozbiehaní vlastných projektov – iniciatívu, ochotu riskovať, inovatívne myslenie a riešenie problémov. Keďže podnikavosť nie je len o firmách, test zahŕňa aj tvoju angažovanosť v komunite a spoločnosti. Zaberie ti približne 3 minúty a výsledok uvidíš hneď.',
        faq: [
            {
                question: 'Čo meria test podnikavosti?',
                answer: 'Sedem oblastí: iniciatívu, ochotu riskovať, inováciu a riešenie problémov ako podnikateľské predpoklady a komunitnú angažovanosť, sociálnu zodpovednosť a demokratickú participáciu ako občiansku angažovanosť.'
            },
            {
                question: 'Prečo test obsahuje aj občiansku angažovanosť?',
                answer: 'Podnikavosť chápeme širšie ako založenie firmy – ako schopnosť všimnúť si problém a začať ho riešiť, či už v startupe, v zamestnaní, alebo vo svojej komunite.'
            },
            {
                question: 'Má test zmysel, aj keď nechcem podnikať?',
                answer: 'Áno. Podnikavé myslenie sa hodí aj v zamestnaní pri presadzovaní nových nápadov, v neziskovej organizácii či v dobrovoľníctve. Neexistujú správne ani nesprávne odpovede.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 35 otázok a zaberie asi 3 minúty. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok uvidíš hneď po vyplnení.'
            },
            {
                question: 'Kto uvidí moje výsledky?',
                answer: 'Výsledky vidíš iba ty. Keď sa cez GROWNi uchádzaš o prácu, môžeš sa dobrovoľne rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov dôležitých pre kariéru.'
            }
        ],
        related: ['index-excelencie', 'karierne-zaujmy', 'chut-pomoct-slovensku']
    },

    'index-excelencie': {
        title: 'Index excelencie zadarmo – vytrvalosť a sebarozvoj | GROWNi',
        description:
            'Index excelencie: krátky test na 3 minúty. Zmeria tvoju vytrvalosť, adaptabilitu, schopnosť učiť sa, štandardy kvality a neustále zlepšovanie. Zadarmo.',
        keywords: [
            'index excelencie',
            'test vytrvalosti',
            'grit test',
            'test návykov úspešných ľudí',
            'schopnosť učiť sa test'
        ],
        intro: 'Index excelencie meria návyky a postoje, ktoré podľa výskumov odlišujú dlhodobo výnimočný výkon od priemeru. Vychádza z výskumov vedomého tréningu (Anders Ericsson), vytrvalosti známej ako grit (Angela Duckworth) či stavu flow (Mihály Csíkszentmihályi) a za približne 3 minúty ti ukáže skóre v piatich oblastiach aj celkový index.',
        faq: [
            {
                question: 'Čo meria Index excelencie?',
                answer: 'Päť oblastí: adaptabilitu, vytrvalosť, schopnosť učiť sa, štandardy kvality a neustále zlepšovanie. Okrem skóre v každej oblasti dostaneš aj celkový index na škále od 0 do 100.'
            },
            {
                question: 'Je to test gritu?',
                answer: 'Vytrvalosť, ktorú Angela Duckworth nazýva grit, je jednou z piatich oblastí indexu. Index je však širší a nie je to oficiálna škála Grit Scale.'
            },
            {
                question: 'Ako test prebieha?',
                answer: 'Odpovieš na 30 tvrdení o tom, ako pristupuješ k práci a učeniu, doplnených o niekoľko krátkych modelových situácií. Situácie lepšie zachytia skutočné správanie, preto majú vo výsledku väčšiu váhu.'
            },
            {
                question: 'Aký je rozdiel oproti testu rastového myslenia?',
                answer: 'Rastové myslenie meria tvoje presvedčenie, že schopnosti sa dajú rozvíjať. Index excelencie meria konkrétne návyky a štandardy, s ktorými na sebe pracuješ, takže sa dobre dopĺňajú.'
            },
            {
                question: 'Je test zadarmo a kto uvidí výsledky?',
                answer: 'Test je zadarmo, stačí bezplatný účet na GROWNi. Výsledky vidíš iba ty, no keď sa cez GROWNi uchádzaš o prácu, môžeš sa rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov dôležitých pre kariéru.'
            }
        ],
        related: ['rastove-myslenie', 'podnikavost', 'motivacia']
    },

    'chut-pomoct-slovensku': {
        title: 'Chuť pomôcť Slovensku – test angažovanosti zadarmo | GROWNi',
        description:
            'Test občianskej angažovanosti: 20 tvrdení, asi 8 minút. Zistíš, aká je tvoja chuť zapojiť sa do zlepšovania Slovenska a aký ďalší krok ti sedí.',
        keywords: [
            'test občianskej angažovanosti',
            'ako pomôcť Slovensku',
            'občianska angažovanosť',
            'chcem pomáhať',
            'dobrovoľníctvo test'
        ],
        intro: 'Test Chuť pomôcť Slovensku meria tvoju občiansku angažovanosť – nakoľko veríš, že sa veci dajú zmeniť, či máš okolo seba ľudí na spoluprácu a ako často pomáhaš iným bez nároku na odmenu. Za približne 8 minút získaš výsledok s odporúčaním, ktorým smerom sa zapojiť: cez dobrovoľníctvo, prácu alebo verejný sektor.',
        faq: [
            {
                question: 'Čo test meria?',
                answer: 'Tvoju ochotu a pripravenosť zapojiť sa do zlepšovania spoločnosti: či veríš, že bežný človek môže niečo zmeniť, či poznáš ľudí, ktorí na tom pracujú, a ako často pomáhaš bez nároku na odmenu.'
            },
            {
                question: 'Čo mi výsledok povie?',
                answer: 'Výsledok ťa zaradí do jednej z troch úrovní – od toho, že sa ešte potrebuješ posmeliť, až po to, že ťa už nič nezastaví. Ku každej dostaneš odporúčaný ďalší krok na GROWNi, napríklad dobrovoľníctvo alebo prácu vo verejnom sektore.'
            },
            {
                question: 'Musím mať skúsenosti s dobrovoľníctvom?',
                answer: 'Nie. Test je pre každého, koho zaujíma, ako prispieť k lepšiemu Slovensku – aj keď zatiaľ nevie, kde začať. Neexistujú správne ani nesprávne odpovede.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 tvrdení a zaberie približne 8 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['podnikavost', 'hodnoty', 'charakterove-silne-stranky']
    },

    'styly-vedenia': {
        title: 'Test štýlu vedenia zadarmo – 6 štýlov Golemana | GROWNi',
        description:
            'Test štýlu vedenia podľa Daniela Golemana: 20 situácií, asi 10 minút. Zistíš svoj dominantný štýl zo šiestich a ako vplýva na klímu v tíme. Zadarmo.',
        keywords: [
            'test štýlu vedenia',
            'štýly vedenia Goleman',
            'leadership test',
            'aký som líder',
            'štýly riadenia ľudí'
        ],
        intro: 'Test štýlu vedenia vychádza z modelu šiestich štýlov Daniela Golemana: vizionárskeho, koučovacieho, afiliačného, demokratického, udávajúceho tempo a direktívneho. V 20 situáciách z vedenia tímu vyberieš reakciu, ktorá je ti najprirodzenejšia, a uvidíš svoj dominantný štýl aj rozloženie medzi všetkými šiestimi.',
        faq: [
            {
                question: 'Aké sú Golemanove štýly vedenia?',
                answer: 'Vizionársky (inšpiruje víziou), koučovací (rozvíja ľudí), afiliačný (buduje vzťahy a harmóniu), demokratický (hľadá konsenzus), udávajúci tempo (vedie príkladom a vysokou latkou) a direktívny (dáva jasné pokyny v kríze).'
            },
            {
                question: 'Ktorý štýl vedenia je najlepší?',
                answer: 'Žiadny. Každý štýl má situácie, v ktorých funguje najlepšie, aj svoje riziká – direktívny pomáha v skutočnej kríze, no dlhodobo dusí iniciatívu. Najúčinnejší lídri podľa Golemana flexibilne striedajú viaceré štýly.'
            },
            {
                question: 'Je test len pre manažérov?',
                answer: 'Nie. Situácie sa týkajú vedenia tímu, no test sa hodí aj pre vedúcich projektov, budúcich lídrov či každého, kto chce lepšie pochopiť, ako pôsobí na ľudí okolo seba.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 situačných otázok a zaberie asi 10 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi a výsledok aj odporúčania na rozvoj uvidíš hneď.'
            },
            {
                question: 'Kto uvidí moje výsledky?',
                answer: 'Výsledky vidíš iba ty. Keď sa cez GROWNi uchádzaš o prácu, môžeš sa dobrovoľne rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov dôležitých pre kariéru.'
            }
        ],
        related: ['organizacne-paradigmy', 'urovne-pocuvania', 'dospely-vyvoj']
    },

    'organizacne-paradigmy': {
        title: 'Test organizačnej kultúry – 5 farieb podľa Laloux | GROWNi',
        description:
            'Test organizačnej kultúry podľa Frédérica Laloux: 20 otázok, asi 8 minút. Zistíš, v ktorej z 5 farieb – od červenej po tyrkysovú – funguje tvoja firma.',
        keywords: [
            'test organizačnej kultúry',
            'Laloux',
            'tyrkysová organizácia',
            'Reinventing Organizations',
            'teal organizácia',
            'organizačná kultúra'
        ],
        intro: 'Test organizačnej kultúry vychádza z knihy Reinventing Organizations (2014), v ktorej Frédéric Laloux opísal päť vývojových paradigiem organizácií – červenú, jantárovú, oranžovú, zelenú a tyrkysovú. Na 20 otázok o rozhodovaní, motivácii a riešení konfliktov odpovieš podľa svojej organizácie a uvidíš, v akej farbe najčastejšie funguje.',
        faq: [
            {
                question: 'Čo znamenajú farby v Lalouxovom modeli?',
                answer: 'Červená stojí na moci silného lídra, jantárová na pravidlách a pevnej hierarchii, oranžová na výkone, cieľoch a KPI, zelená na hodnotách a ľuďoch a tyrkysová na samoriadení a spoločnom zmysle. Každá farba je iný spôsob, ako organizácia rozhoduje a motivuje ľudí.'
            },
            {
                question: 'Čo je tyrkysová organizácia?',
                answer: 'Organizácia, ktorá funguje ako živý systém: bez pevnej hierarchie, so samoriadiacimi tímami, rozhodovaním cez poradný proces a vlastným evolučným zmyslom. Laloux uvádza príklady ako holandská Buurtzorg alebo americká Morning Star.'
            },
            {
                question: 'Je tyrkysová kultúra lepšia ako ostatné?',
                answer: 'Nie automaticky. Každá paradigma má svoje silné stránky aj limity a tyrkysové praktiky vyžadujú vysokú zrelosť ľudí aj vedenia. Výsledok ti pomôže pochopiť, kde je tvoja organizácia teraz a aký ďalší krok dáva zmysel.'
            },
            {
                question: 'Hodnotím seba, alebo svoju organizáciu?',
                answer: 'Organizáciu. Odpovedáš podľa toho, ako to naozaj funguje vo firme, škole, neziskovke alebo tíme, v ktorom pôsobíš – nie podľa toho, ako by to malo byť.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 otázok a zaberie asi 8 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['styly-vedenia', 'urovne-vedomia', 'hodnoty']
    },

    'urovne-vedomia': {
        title: 'Úrovne vedomia podľa Kegana – test zadarmo | GROWNi',
        description:
            'Test úrovní vedomia podľa Roberta Kegana: 20 otázok, asi 10 minút. Zistíš, ako konštruuješ zmysel seba, vzťahov a sveta – od úrovne 2 po 5. Zadarmo.',
        keywords: [
            'úrovne vedomia',
            'Kegan orders of consciousness',
            'Keganove úrovne vedomia',
            'socializovaná myseľ',
            'sebaautorská myseľ'
        ],
        intro: 'Test úrovní vedomia vychádza z teórie Roberta Kegana z Harvardu o úrovniach vedomia (orders of consciousness), teda o rastúcej komplexite, s akou dospelý človek rozumie sebe a svetu. Na 20 otázok o hodnotách, kritike, identite či rozhodovaní odpovieš podľa seba a zistíš, ktorá z úrovní 2 až 5 je u teba dominantná.',
        faq: [
            {
                question: 'Čo znamenajú Keganove úrovne vedomia?',
                answer: 'Úroveň 2 (imperiálna myseľ) sa riadi vlastnými potrebami, úroveň 3 (socializovaná myseľ) očakávaniami dôležitých ľudí a skupín, úroveň 4 (seba-autorská myseľ) vlastným vnútorným kompasom a úroveň 5 (seba-transformujúca myseľ) dokáže skúmať aj tento kompas. Úroveň 1 patrí ranému detstvu, preto ju test nemeria.'
            },
            {
                question: 'Je to to isté ako Hawkinsova mapa vedomia?',
                answer: 'Nie. Kegan je vývojový psychológ a jeho úrovne opisujú, ako človek konštruuje zmysel seba a vzťahov. Hawkinsova stupnica vychádza z iného, duchovného rámca a tento test ju nemeria.'
            },
            {
                question: 'Aký je rozdiel oproti testu Dospelý vývoj?',
                answer: 'Oba testy vychádzajú z Keganovej teórie. Úrovne vedomia sa pýtajú priamo, ako prežívaš hodnoty, kritiku či vlastnú identitu, kým Dospelý vývoj stojí na príbehových situáciách zo života – spolu ti dajú presnejší obraz.'
            },
            {
                question: 'Ako mám výsledok čítať?',
                answer: 'Uvidíš svoju dominantnú úroveň aj rozloženie medzi všetkými štyrmi. Byť na úrovni 3 je bežné a v poriadku. Kegan sám úrovne zisťuje podrobným rozhovorom, preto výsledok dotazníka ber ako orientačný obraz na sebareflexiu.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 otázok a zaberie asi 10 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['dospely-vyvoj', 'moralny-vyvoj', 'organizacne-paradigmy']
    },

    'urovne-pocuvania': {
        title: 'Úrovne počúvania – test aktívneho počúvania zadarmo | GROWNi',
        description:
            'Test úrovní počúvania podľa koučingového modelu CTI: 20 situácií, asi 6 minút. Zistíš, či počúvaš skôr seba, druhého, alebo celý rozhovor. Zadarmo.',
        keywords: [
            'úrovne počúvania',
            'aktívne počúvanie test',
            'test počúvania',
            'CTI úrovne počúvania',
            'koučovacie počúvanie'
        ],
        intro: 'Test úrovní počúvania vychádza z modelu troch úrovní počúvania, ktorý v koučingu používa Co-Active Training Institute (CTI). Na 20 bežných situáciách z práce aj súkromia zistíš, či počas rozhovoru počúvaš najmä vlastné myšlienky, sústredíš sa naplno na druhého, alebo vnímaš celé dianie vrátane nevyslovených signálov.',
        faq: [
            {
                question: 'Aké sú tri úrovne počúvania?',
                answer: 'Úroveň 1 je interné počúvanie – počúvaš cez vlastné myšlienky a plánuješ odpoveď. Úroveň 2 je sústredené počúvanie, keď sa naplno venuješ druhému človeku. Úroveň 3 je globálne počúvanie, pri ktorom vnímaš aj atmosféru, reč tela a to, čo nebolo povedané.'
            },
            {
                question: 'Je úroveň 1 zlá?',
                answer: 'Nie. Úrovne nie sú lepšie ani horšie a každá má svoje miesto. Úroveň 1 je však predvolený režim väčšiny ľudí, a keď v nej zostaneš, môže ti uniknúť, čo druhý naozaj hovorí.'
            },
            {
                question: 'Ako súvisí test s aktívnym počúvaním?',
                answer: 'Aktívne počúvanie – plná pozornosť, doplňujúce otázky a parafrázovanie – zodpovedá najmä úrovni 2. Test ti ukáže, ako často sa na nej ocitáš a kedy skĺzavaš späť k vlastným myšlienkam.'
            },
            {
                question: 'Ako môžem počúvanie zlepšiť?',
                answer: 'Spolu s výsledkom dostaneš praktické cvičenia, napríklad pred vlastnou odpoveďou parafrázovať, čo zaznelo, alebo si nápad na radu zapísať a vrátiť sa k počúvaniu. Zručnosť sa zlepšuje najmä tréningom v bežných rozhovoroch.'
            },
            {
                question: 'Koľko trvá test a je zadarmo?',
                answer: 'Test má 20 situačných otázok a zaberie asi 6 minút. Je zadarmo, potrebuješ len bezplatný účet na GROWNi. Výsledok uvidíš hneď a vidíš ho iba ty.'
            }
        ],
        related: ['styly-vedenia', 'vztahove-styly', 'urovne-vedomia']
    }
}

export const SEBAPOZNANIE_HUB_SEO: HubSeoContent = {
    title: 'Test osobnosti zadarmo – testy sebapoznania online | GROWNi',
    description:
        'Test osobnosti zadarmo a ďalšie testy sebapoznania: Big Five, kariérne záujmy, hodnoty, silné stránky či motivácia. Výsledok uvidíš hneď po vyplnení.',
    h1: 'Testy osobnosti zadarmo – zisti, čo ti sedí',
    subtitle:
        'Krátke testy sebapoznania ti pomôžu pomenovať silné stránky, hodnoty, motiváciu aj kariérny smer. Vyber si test podľa otázky, ktorú práve riešiš.',
    faq: [
        {
            question: 'Sú testy osobnosti na GROWNi naozaj zadarmo?',
            answer: 'Áno, všetky testy sú zadarmo. Potrebuješ len bezplatný účet na GROWNi, do ktorého sa ti výsledky uložia, a uvidíš ich hneď po dokončení testu.'
        },
        {
            question: 'Ktorý test osobnosti si mám vybrať?',
            answer: 'Na rýchly prehľad o osobnosti sa hodí Big Five (asi 3 minúty), na podrobnejší profil Osobnostný dotazník (asi 15 minút). Ak riešiš kariéru, začni Kariérnymi záujmami. Ak ťa zaujíma, čo ťa poháňa, vyskúšaj Hodnoty alebo Motiváciu.'
        },
        {
            question: 'Je to MBTI alebo 16 Personalities?',
            answer: 'Nie, test MBTI ani 16 Personalities nemáme. MBTI je populárny a mnohým pomáha začať premýšľať o sebe, no zaraďuje ľudí do 16 typov a pri opakovanom vyplnení často vyjde iný typ. Používame preto model Big Five, ktorý má vo výskume silnejšiu oporu – vyskúšaj test Big Five alebo podrobnejší Osobnostný dotazník.'
        },
        {
            question: 'Ako súvisí Big Five so 4 temperamentmi – sangvinik, cholerik, flegmatik, melancholik?',
            answer: 'Štyri temperamenty sú stará typológia, ktorú psychológ Hans Eysenck prepojil s dvoma rozmermi: extraverziou a emocionálnou stabilitou. Napríklad sangvinik je extrovertný a stabilný, melancholik introvertný a menej stabilný. Samostatný test temperamentu nemáme, no oba rozmery zmeria test Big Five.'
        },
        {
            question: 'Kto uvidí moje výsledky?',
            answer: 'Výsledky vidíš iba ty. Keď sa cez GROWNi uchádzaš o prácu, môžeš sa dobrovoľne rozhodnúť ukázať zamestnávateľovi zhrnutie výsledkov, ktoré sú dôležité pre kariéru.'
        },
        {
            question: 'Sú testy vedecky podložené?',
            answer: 'Väčšina testov vychádza z overených psychologických modelov, ako sú Big Five, Hollandov model RIASEC, Schwartzove hodnoty, teória sebadeterminácie či práce Kohlberga a Kegana. Sú to nástroje na sebapoznanie, nie klinická diagnostika, a nenahrádzajú psychológa.'
        },
        {
            question: 'Ako dlho testy trvajú?',
            answer: 'Od 3 do 15 minút. Najrýchlejšie sú Big Five, Podnikavosť a Index excelencie (asi 3 minúty), najdlhšie Osobnostný dotazník, Kariérne záujmy a Morálny vývoj (asi 15 minút).'
        },
        {
            question: 'Môžem test zopakovať?',
            answer: 'Áno, každý test môžeš kedykoľvek zopakovať a predchádzajúce výsledky ti zostanú uložené. Neexistujú správne ani nesprávne odpovede, stačí odpovedať úprimne.'
        }
    ]
}
